import { type Deck, type Finding, flattenDeck } from "@hali-shares/deck-schema";
import { blockText } from "./model";

export { checkDeck, type Finding, type Target } from "@hali-shares/deck-schema";

function label(deck: Deck, blockId: string, text: string): string {
  const n = flattenDeck(deck).findIndex((s) => s.kind === "block" && s.blockId === blockId) + 1;
  return `Slide ${n} · ${text.slice(0, 48)}`;
}

/* ---------- pins on blank space ---------- */

/** Pixels of an image, small enough to scan quickly. */
export type Pixels = { width: number; height: number; data: Uint8ClampedArray };

/**
 * How much is going on around a point: the spread (standard deviation, 0–255) of brightness in a square around
 * (x, y), given as fractions of the image. Plain background scores near 0; text, lines and boxes score well above.
 */
export function busyness(img: Pixels, x: number, y: number, radius = 0.03): number {
  const r = Math.max(2, Math.round(radius * img.width));
  const cx = Math.round(x * (img.width - 1));
  const cy = Math.round(y * (img.height - 1));
  let n = 0;
  let sum = 0;
  let sq = 0;
  for (let py = Math.max(0, cy - r); py <= Math.min(img.height - 1, cy + r); py++)
    for (let px = Math.max(0, cx - r); px <= Math.min(img.width - 1, cx + r); px++) {
      const i = (py * img.width + px) * 4;
      const l = 0.299 * img.data[i]! + 0.587 * img.data[i + 1]! + 0.114 * img.data[i + 2]!;
      n++;
      sum += l;
      sq += l * l;
    }
  if (n === 0) return 0;
  const mean = sum / n;
  return Math.sqrt(Math.max(0, sq / n - mean * mean));
}

export const BLANK_BELOW = 4;

// An uploaded image never changes (a new upload is a new id), so each is decoded once per page.
const pixelCache = new Map<string, Promise<Pixels>>();

function loadPixels(assetId: string): Promise<Pixels> {
  let p = pixelCache.get(assetId);
  if (!p) {
    p = decodePixels(assetId);
    p.catch(() => pixelCache.delete(assetId)); // try again next time
    pixelCache.set(assetId, p);
  }
  return p;
}

async function decodePixels(assetId: string): Promise<Pixels> {
  const img = new Image();
  img.src = `/api/assets/${assetId}`;
  await img.decode();
  const scale = Math.min(1, 400 / img.naturalWidth);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const { width, height, data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  return { width, height, data };
}

/** Pins that sit on empty background, where zooming in would show the room nothing. */
export async function checkPins(
  deck: Deck,
  load: (assetId: string) => Promise<Pixels> = loadPixels,
): Promise<Finding[]> {
  const out: Finding[] = [];
  const jobs = deck.sections.flatMap((s, si) =>
    s.blocks.flatMap((b, bi) => (b.type === "screenshot" && b.assetId && b.pins.length ? [{ b, si, bi }] : [])),
  );
  for (const { b, si, bi } of jobs) {
    if (b.type !== "screenshot" || !b.assetId) continue;
    let px: Pixels;
    try {
      px = await load(b.assetId);
    } catch {
      continue;
    }
    const blank = b.pins.flatMap((p, i) => (busyness(px, p.x, p.y) < BLANK_BELOW ? [i + 1] : []));
    if (blank.length)
      out.push({
        level: "fix",
        where: label(deck, b.id, blockText(b) || b.type),
        message: `Pin ${blank.join(", ")} ${blank.length === 1 ? "sits" : "sit"} on empty space: drag ${blank.length === 1 ? "it" : "them"} onto what the question is about`,
        target: { kind: "block", sectionIndex: si, blockIndex: bi },
      });
  }
  return out;
}
